import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  Avatar,
  IconButton,
  Card,
  CardContent,
  Chip,
  Stack,
  Fade,
  Slide,
  Collapse,
  LinearProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  Send as SendIcon,
  BugReport as BugIcon,
  Translate as TranslateIcon,
  ShoppingCart as PurchaseIcon,
  Speed as PerformanceIcon,
  Brush as UIIcon,
  Build as FeatureIcon,
  Lightbulb as SuggestionIcon,
  Business as CooperationIcon,
  Help as OtherIcon,
  Delete as DeleteIcon,
  Support as SupportIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { sendMessage } from "../services/websocketManager";
import { generalMessageTemplate } from "../entities/generalMessageTemplate";
import { GENERAL } from "../general/General";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const issueTypes = [
  { 
    value: "invalid_translation", 
    label: "Invalid Translation",
    icon: <TranslateIcon />,
    color: "#9c27b0",
    description: "Report translation errors or suggest improvements"
  },
  { 
    value: "game_bug", 
    label: "Game issue",
    icon: <BugIcon />,
    color: "#f44336",
    description: "Report bugs or problems with game functionality"
  },
  { 
    value: "purchase_bug", 
    label: "Purchase Process issue",
    icon: <PurchaseIcon />,
    color: "#ff9800",
    description: "Issues with payment or purchase process"
  },
  { 
    value: "performance_issue", 
    label: "Performance Issue",
    icon: <PerformanceIcon />,
    color: "#e91e63",
    description: "Slow loading, lag, or performance problems"
  },
  { 
    value: "ui_problem", 
    label: "UI/UX issue",
    icon: <UIIcon />,
    color: "#3f51b5",
    description: "Problems with user interface or user experience"
  },
  { 
    value: "feature_not_working", 
    label: "Feature Not Working",
    icon: <FeatureIcon />,
    color: "#607d8b",
    description: "A specific feature is broken or not working"
  },
  { 
    value: "suggestion", 
    label: "Suggestion / Feedback",
    icon: <SuggestionIcon />,
    color: "#4caf50",
    description: "Share ideas for improvements or new features"
  },
  { 
    value: "cooperation", 
    label: "Cooperation",
    icon: <CooperationIcon />,
    color: "#2196f3",
    description: "Business partnerships or collaboration inquiries"
  },
  { 
    value: "other", 
    label: "Other",
    icon: <OtherIcon />,
    color: "#795548",
    description: "General questions or other topics"
  },
  { 
    value: "delete_account", 
    label: "Delete my account",
    icon: <DeleteIcon />,
    color: "#424242",
    description: "Request account deletion and data removal"
  },
];

const ReportIssueForm = ({ onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const [issueType, setIssueType] = useState("");
  const [language, setLanguage] = useState("en");
  const [currentMessage, setCurrentMessage] = useState("");
  const [expectedMessage, setExpectedMessage] = useState("");
  const [description, setDescription] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const steps = [
    t("Select Issue Type"),
    t("Provide Details"),
    t("Submit Report")
  ];

  const selectedIssue = issueTypes.find(type => type.value === issueType);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate("/main");
    }
  };

  const handleSubmit = async () => {
    if (!issueType) return;

    setIsSubmitting(true);
    setCurrentStep(2);

    let generalMessage = { ...generalMessageTemplate };
    generalMessage.messageType = "report_issue";
    generalMessage.issueReport.issueType = issueType;
    generalMessage.issueReport.language = language;
    generalMessage.issueReport.currentMessage = currentMessage;
    generalMessage.issueReport.expectedMessage = expectedMessage;
    generalMessage.issueReport.description = description;
    generalMessage.issueReport.timestamp = new Date().toISOString();
    
    // Simulate API call
    setTimeout(() => {
      sendMessage(generalMessage);
      setIsSubmitting(false);
      setSubmitted(true);
      
      // Reset form after delay
      setTimeout(() => {
        setIssueType("");
        setLanguage("en");
        setCurrentMessage("");
        setExpectedMessage("");
        setDescription("");
        setCurrentStep(0);
        setSubmitted(false);
      }, 3000);
    }, 2000);
  };

  const isFormValid = () => {
    if (!issueType) return false;
    if (issueType === "invalid_translation") {
      return currentMessage.trim() && expectedMessage.trim();
    }
    return description.trim();
  };

  const handleIssueSelect = (value) => {
    setIssueType(value);
    setCurrentStep(1);
  };

  if (submitted) {
    return (
      <Fade in>
        <Box
          sx={{
            maxWidth: 500,
            mx: "auto",
            mt: 4,
            p: 3,
            textAlign: "center",
          }}
        >
          <Card
            sx={{
              background: "linear-gradient(135deg, #4caf50 0%, #45a049 100%)",
              color: "white",
              borderRadius: 4,
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <CheckIcon sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h5" fontWeight="bold" mb={2}>
                {t("Thank You!")}
              </Typography>
              <Typography variant="body1">
                {t("Your message to support has been received—thank you!")}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Fade>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        p: isMobile ? 1 : 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Card
        sx={{
          maxWidth: 600,
          width: "100%",
          borderRadius: 4,
          overflow: "hidden",
          position: "relative",
        }}
        elevation={24}
      >
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            background: "linear-gradient(45deg, #1a1a1a 30%, #2d2d2d 90%)",
            color: "white",
            p: 3,
            position: "relative",
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "white",
              backgroundColor: "rgba(255,255,255,0.1)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Avatar
              sx={{
                backgroundColor: "primary.main",
                width: 48,
                height: 48,
              }}
            >
              <SupportIcon />
            </Avatar>
            
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {t("Contact Support")}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {t("We're here to help you")}
              </Typography>
            </Box>
          </Box>

          {/* Progress Stepper */}
          <Stepper 
            activeStep={currentStep} 
            sx={{
              "& .MuiStepLabel-label": { 
                color: "white !important",
                fontSize: "0.8rem"
              },
              "& .MuiStepIcon-root": { 
                color: "rgba(255,255,255,0.3)",
                "&.Mui-active": { color: "white" },
                "&.Mui-completed": { color: "#4caf50" }
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        <CardContent sx={{ p: 3 }}>
          {/* Step 1: Issue Type Selection */}
          <Collapse in={currentStep === 0}>
            <Typography variant="h6" mb={3} textAlign="center">
              {t("What can we help you with?")}
            </Typography>
            
            <Stack spacing={1.5}>
              {issueTypes.map((type, index) => (
                <Fade key={type.value} in timeout={400 + index * 100}>
                  <Card
                    sx={{
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      border: "2px solid transparent",
                      "&:hover": {
                        transform: "translateX(8px)",
                        boxShadow: `0 4px 20px ${type.color}30`,
                        borderColor: type.color,
                      },
                    }}
                    onClick={() => handleIssueSelect(type.value)}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Avatar
                          sx={{
                            backgroundColor: `${type.color}15`,
                            color: type.color,
                            width: 40,
                            height: 40,
                          }}
                        >
                          {type.icon}
                        </Avatar>
                        
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {t(type.label)}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ fontSize: "0.85rem" }}
                          >
                            {t(type.description)}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              ))}
            </Stack>
          </Collapse>

          {/* Step 2: Details Form */}
          <Collapse in={currentStep === 1}>
            <Slide direction="left" in={currentStep === 1}>
              <Box>
                {selectedIssue && (
                  <Card
                    sx={{
                      mb: 3,
                      background: `linear-gradient(135deg, ${selectedIssue.color}15 0%, ${selectedIssue.color}05 100%)`,
                      border: `1px solid ${selectedIssue.color}30`,
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Avatar
                          sx={{
                            backgroundColor: selectedIssue.color,
                            color: "white",
                            width: 32,
                            height: 32,
                          }}
                        >
                          {selectedIssue.icon}
                        </Avatar>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {t(selectedIssue.label)}
                        </Typography>
                        {/* <Button
                          size="small"
                          onClick={() => setCurrentStep(0)}
                          sx={{ ml: "auto" }}
                        >
                          {t("Change")}
                        </Button> */}
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {issueType === "invalid_translation" ? (
                  <Stack spacing={3}>
                    <FormControl fullWidth>
                      <InputLabel>{t("Language")}</InputLabel>
                      <Select
                        value={language}
                        label={t("Language")}
                        onChange={(e) => setLanguage(e.target.value)}
                      >
                        {GENERAL.languageOptions.map((lang) => (
                          <MenuItem key={lang.code} value={lang.code}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                              <Avatar
                                src={lang.flag}
                                alt={lang.name}
                                sx={{ width: 24, height: 24 }}
                              />
                              <Typography>{lang.name}</Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label={t("Text with translation issue")}
                      value={currentMessage}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        if (newValue.length <= 300) {
                          setCurrentMessage(newValue);
                        }
                      }}
                      helperText={`${currentMessage.length}/300`}
                      variant="outlined"
                    />

                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label={t("Correct Translation")}
                      value={expectedMessage}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        if (newValue.length <= 300) {
                          setExpectedMessage(newValue);
                        }
                      }}
                      helperText={`${expectedMessage.length}/300`}
                      variant="outlined"
                    />
                  </Stack>
                ) : (
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label={t("Description")}
                    placeholder={t("Please describe your issue in detail...")}
                    value={description}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      if (newValue.length <= 1000) {
                        setDescription(newValue);
                      }
                    }}
                    helperText={`${description.length}/1000`}
                    variant="outlined"
                  />
                )}

                <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setCurrentStep(0)}
                    sx={{ flex: 1 }}
                  >
                    {t("Back")}
                  </Button>
                  
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!isFormValid()}
                    startIcon={<SendIcon />}
                    sx={{
                      flex: 2,
                      background: selectedIssue 
                        ? `linear-gradient(45deg, ${selectedIssue.color} 30%, ${selectedIssue.color}AA 90%)`
                        : undefined,
                    }}
                  >
                    {t("Send Report")}
                  </Button>
                </Box>
              </Box>
            </Slide>
          </Collapse>

          {/* Step 3: Submitting */}
          <Collapse in={currentStep === 2 && isSubmitting}>
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  mx: "auto",
                  mb: 2,
                  backgroundColor: "primary.main",
                }}
              >
                <SendIcon sx={{ fontSize: 32 }} />
              </Avatar>
              
              <Typography variant="h6" mb={2}>
                {t("Sending your report...")}
              </Typography>
              
              <LinearProgress
                sx={{
                  width: 200,
                  mx: "auto",
                  mb: 2,
                  borderRadius: 2,
                  height: 8,
                }}
              />
              
              <Typography variant="body2" color="text.secondary">
                {t("Please wait while we process your request")}
              </Typography>
            </Box>
          </Collapse>
        </CardContent>

        {/* Footer Links */}
        <Divider />
        <Box sx={{ p: 2, textAlign: "center", backgroundColor: "grey.50" }}>
          <Typography variant="body2" color="text.secondary">
            <a
              href="https://landing.democraticchess.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                textDecoration: "none", 
                color: theme.palette.primary.main,
                marginRight: 16 
              }}
            >
              {t("Privacy Policy")}
            </a>
            <a
              href="https://landing.democraticchess.com/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                textDecoration: "none", 
                color: theme.palette.primary.main,
                marginRight: 16 
              }}
            >
              {t("Terms of Service")}
            </a>
            <a
              href="https://landing.democraticchess.com/data-deletion"
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                textDecoration: "none", 
                color: theme.palette.primary.main 
              }}
            >
              {t("Data Deletion")}
            </a>
          </Typography>
        </Box>
      </Card>
    </Box>
  );
};

export default ReportIssueForm;